import React, { useMemo } from 'react';
import {
  filterActions, actionFilterOptions, ACTION_STATUSES, ACTION_STATUS_LABEL,
} from '../../lib/projectDebriefs';
import { fmtDate } from '../../lib/dates';

const FONT = "font-['Metropolis']";

// Every action from every debrief, in one list (project-debriefs §4).
//
// An action is a note somebody decided to do something about, so the note's
// words ARE the action — there is no separate title to show. The list leads
// with those words and puts owner / when / topic / source project around them,
// rather than showing a bare owner and status, which would be a to-do list with
// the reason stripped out.
//
// Status is a segmented control rather than a dropdown: three states, and the
// whole point of this screen is closing things off, so it should take one click.

const STATUS_STYLE = {
  open:    'text-[#7A4E10] bg-[#E89A2E]/12 border-[#E89A2E]/40',
  done:    'text-fusion-forest bg-[#92D400]/12 border-[#92D400]/40',
  dropped: 'text-[#51534A] bg-[#51534A]/8 border-[#DADCCD]',
};

// Notes are authored as rich text. Strip to plain for this list so a long
// formatted note can't blow the row height apart, but keep the full text in
// the title attribute.
const plain = (html) => String(html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

export default function DebriefActionsList({
  actions = [], filters, onFilters, busyId = null, onSetStatus, onOpenDebrief,
}) {
  const options = useMemo(() => actionFilterOptions(actions), [actions]);
  const visible = useMemo(() => filterActions(actions, filters), [actions, filters]);
  const set = (patch) => onFilters?.({ ...filters, ...patch });

  const selectCls = `${FONT} bg-white border border-line rounded-lg px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-[#92D400]`;

  return (
    <div>
      <div className="flex items-end gap-2.5 flex-wrap mb-4">
        <div className="min-w-[180px] flex-[2]">
          <label htmlFor="act-search" className={`${FONT} block text-[11px] font-semibold text-[#51534A] mb-1`}>Search</label>
          <input
            id="act-search"
            value={filters.search}
            onChange={e => set({ search: e.target.value })}
            placeholder="Action, owner, project…"
            className={`${selectCls} w-full`} />
        </div>
        <div>
          <label htmlFor="act-status" className={`${FONT} block text-[11px] font-semibold text-[#51534A] mb-1`}>Status</label>
          <select id="act-status" value={filters.status} onChange={e => set({ status: e.target.value })} className={selectCls}>
            <option value="">All</option>
            {ACTION_STATUSES.map(s => <option key={s} value={s}>{ACTION_STATUS_LABEL[s] || s}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="act-owner" className={`${FONT} block text-[11px] font-semibold text-[#51534A] mb-1`}>Owner</label>
          <select id="act-owner" value={filters.owner} onChange={e => set({ owner: e.target.value })} className={selectCls}>
            <option value="">Anyone</option>
            {options.owners.map(o => <option key={o} value={o}>{o}</option>)}
            {/* An action nobody owns is the one most worth finding. */}
            {options.hasUnowned && <option value="__none__">No owner</option>}
          </select>
        </div>
        <div>
          <label htmlFor="act-topic" className={`${FONT} block text-[11px] font-semibold text-[#51534A] mb-1`}>Topic</label>
          <select id="act-topic" value={filters.topic} onChange={e => set({ topic: e.target.value })} className={selectCls}>
            <option value="">All topics</option>
            {options.topics.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="bg-white border border-[#E6EADB] rounded-2xl p-6">
          <p className={`${FONT} text-[13px] text-fusion-mid-grey`}>
            {actions.length === 0
              ? 'No actions yet. Turn a note into an action inside a debrief and it will appear here.'
              : 'No actions match those filters.'}
          </p>
        </div>
      ) : (
        <ul className="bg-white border border-[#E6EADB] rounded-2xl overflow-hidden">
          {visible.map(a => {
            const text = plain(a.note_body) || 'Untitled action';
            const busy = busyId === a.id;
            return (
              <li key={a.id} className="flex items-start gap-4 px-5 py-3.5 border-b border-[#F0F2E8] last:border-0">
                <div className="min-w-0 flex-1">
                  <p className={`${FONT} text-[13px] text-ink leading-snug`} title={text}>{text}</p>
                  <div className={`${FONT} text-[11px] text-[#51534A] mt-1 flex items-center gap-x-2 gap-y-1 flex-wrap`}>
                    <span className="font-semibold text-ink">{a.owner || 'No owner'}</span>
                    {a.timeframe && <span>· {a.timeframe}</span>}
                    {a.topic_title && <span>· {a.topic_title}</span>}
                    {a.project_ref && (
                      <>
                        <span aria-hidden="true">·</span>
                        <button
                          type="button"
                          onClick={() => onOpenDebrief?.(a.debrief_id)}
                          className="underline decoration-[#51534A]/30 underline-offset-2 hover:text-fusion-forest hover:decoration-fusion-forest rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-fusion-forest">
                          {a.project_ref}{a.debrief_date ? ` · ${fmtDate(a.debrief_date)}` : ''}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {/* Segmented control, not a dropdown: three states, and closing
                    things off is what this screen is for. aria-pressed carries
                    the state so it isn't conveyed by colour alone. */}
                <div className="flex items-center gap-1 flex-shrink-0" role="group" aria-label={`Status for: ${text}`}>
                  {ACTION_STATUSES.map(s => (
                    <button
                      key={s}
                      type="button"
                      disabled={busy}
                      aria-pressed={a.status === s}
                      onClick={() => a.status !== s && onSetStatus?.(a, s)}
                      className={`${FONT} text-[10px] font-bold uppercase tracking-[0.1em] rounded-full px-2.5 py-1 border transition-colors disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-fusion-forest ${
                        a.status === s ? STATUS_STYLE[s] : 'text-[#9BA08C] bg-white border-[#E6EADB] hover:border-[#BFC7AE]'
                      }`}>
                      {ACTION_STATUS_LABEL[s] || s}
                    </button>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
